import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ReportConfigV2 } from 'src/hooks/reportConfigs';
import { ReportConfig } from 'src/hooks/useReportConfigs';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { GridActionType } from 'src/utils/tableUtils';
import { Feature } from 'src/WebApiController';

import { ReportsTable } from './ReportsTable';
import * as styles from './ReportsTableContainer.css';

export function ReportsTableContainer({
  reports,
  reportsV2,
  onReportAction,
}: {
  reports?: ReportConfig[];
  reportsV2?: ReportConfigV2[];
  onReportAction: (
    report: ReportConfig | undefined,
    reportV2: ReportConfigV2 | undefined,
    actionType: GridActionType,
    event?: React.MouseEvent
  ) => void;
}) {
  const hasActiveInventoryReportRedirectFeature = useUserHasFeature(
    Feature.GlobalReportV1RedirectV2_ActiveInventory
  );
  const hasDailySalesReportRedirectFeature = useUserHasFeature(
    Feature.GlobalReportV1RedirectV2_DailySales
  );
  const hasPurchasedInventoryReportRedirectFeature = useUserHasFeature(
    Feature.GlobalReportV1RedirectV2_PurchasedInventory
  );
  const hasCanceledInventoryReportRedirectFeature = useUserHasFeature(
    Feature.GlobalReportV1RedirectV2_CanceledInventory
  );
  const hasEventPandLReportRedirectFeature = useUserHasFeature(
    Feature.GlobalReportV1RedirectV2_EventPandL
  );
  const hasMarketplacePerformanceReportRedirectFeature = useUserHasFeature(
    Feature.GlobalReportV1RedirectV2_MarketplacePerformance
  );
  const hasPerformerPandLReportRedirectFeature = useUserHasFeature(
    Feature.GlobalReportV1RedirectV2_PerformerPandL
  );
  const hasSoldInventoryReportRedirectFeature = useUserHasFeature(
    Feature.GlobalReportV1RedirectV2_SoldInventory
  );
  const hasCommissionReportRedirectFeature = useUserHasFeature(
    Feature.GlobalReportV1RedirectV2_CommissionReport
  );
  const hasAllReportsRedirectFeature = useUserHasFeature(
    Feature.GlobalReportV1RedirectV2_All
  );

  // Enum values come from GlobalReportType - clean up this mapping when features are GA'ed
  const hasFeatureForGlobalReportMap: Record<number, boolean> = {
    [1]: hasActiveInventoryReportRedirectFeature,
    [2]: hasCanceledInventoryReportRedirectFeature,
    [3]: hasDailySalesReportRedirectFeature,
    [4]: hasEventPandLReportRedirectFeature,
    [5]: hasMarketplacePerformanceReportRedirectFeature,
    [6]: hasPerformerPandLReportRedirectFeature,
    [7]: hasPurchasedInventoryReportRedirectFeature,
    [8]: hasSoldInventoryReportRedirectFeature,
    [9]: hasCommissionReportRedirectFeature,
  };

  const reportsV2Default =
    reportsV2?.filter(
      (r) =>
        r.globalReportTypeId &&
        (!!hasFeatureForGlobalReportMap[r.globalReportTypeId] ||
          hasAllReportsRedirectFeature)
    ) ?? [];
  const reportsV2Custom = reportsV2?.filter((r) => !r.globalReportTypeId) ?? [];

  const reportsDefault =
    reports?.filter(
      (r) =>
        r.globalReportTypeId &&
        !reportsV2Default.some(
          (rv2) => rv2.globalReportTypeId === r.globalReportTypeId
        ) // Avoid duplicates in V2 reports. Show v2 reports when possible
    ) ?? [];
  const reportsCustom = reports?.filter((r) => !r.globalReportTypeId) ?? [];

  return (
    <div className={styles.mainContent}>
      <div className={styles.reportTableContainer}>
        {reportsDefault.length + reportsV2Default.length > 0 && (
          <Stack direction="column">
            <h2
              className={styles.subHeader}
              style={{
                paddingTop: vars.spacing['lg'],
                paddingBottom: vars.spacing['lg'],
              }}
            >
              <Content id={ContentId.DefaultReports} />
            </h2>
            <ReportsTable
              reports={reportsDefault}
              reportsV2={reportsV2Default}
              onReportAction={onReportAction}
              isGlobalSection
              initState={{
                sorting: [{ id: 'reportName', desc: false }],
              }}
            />
          </Stack>
        )}

        <Stack direction="column">
          <h2
            className={styles.subHeader}
            style={{
              paddingTop: vars.spacing['xl'],
              paddingBottom: vars.spacing['lg'],
            }}
          >
            <Content id={ContentId.CustomReports} />
          </h2>
          <ReportsTable
            reports={reportsCustom}
            reportsV2={reportsV2Custom}
            onReportAction={onReportAction}
            initState={{
              sorting: [{ id: 'reportName', desc: false }],
            }}
          />
        </Stack>
      </div>
    </div>
  );
}
